/**
 * Issue job POs from a designer-invoice review (rare client backfill / repair).
 * Day-to-day path: server issues POs only on FINAL approveDesignerInvoice gate.
 * Payment method comes from the invoice queue record — never hard-code ach_office.
 * Card POs skip recordAchPayable (no Billing & Payables row). Bill Payment
 * (`ach_office`) creates supplierBills regardless of line phase.
 */

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import type { DesignerInvoiceDoc, DesignerInvoiceLine } from './designerInvoiceService';
import {
  buildDesignerInvoiceJobPurchaseDraft,
  designerInvoiceQuoteAttachment,
  designerInvoiceSelfIssueStep,
  groupDesignerInvoiceJobPoLines,
  invoiceNeedsJobPurchaseOrders,
  type DesignerInvoiceJobPoGroup,
} from './designerInvoicePurchase';
import {
  designerInvoicePaymentMethodLabel,
  primaryDesignerInvoicePhase,
  resolveDesignerInvoicePaymentMethodId,
  shouldCreateDesignerInvoiceQboBill,
  shouldCreateDesignerInvoiceSupplierBill,
} from './designerInvoicePayment';
import {
  approvePurchaseRequisition,
  getPurchaseRequisition,
  parsePurchaseRequisition,
  PURCHASE_REQUISITIONS_COLLECTION,
  stampPurchaseQbBillStatus,
  submitPurchaseRequisition,
} from './purchasingRequisitions';
import type { PurchaseRequisition } from '../types/purchasing';
import { tryCreateDesignerInvoiceQboBills } from './designerInvoiceService';
import { describePurchaseQbBill } from './purchaseQbBillStatus';
import { findSupplierBillForPurchaseOrder, stampSupplierBillQbLink } from './supplierBillsService';

export type IssuedDesignerInvoicePurchase = {
  purchaseRequisitionId: string;
  poNumber?: string;
  dealId: string;
  projectId: string;
  amount: number;
  reused: boolean;
};

export type IssueDesignerInvoicePurchasesResult = {
  issued: IssuedDesignerInvoicePurchase[];
  qbSummaries: string[];
};

function activeDesignerInvoicePurchase(
  row: PurchaseRequisition,
  group: DesignerInvoiceJobPoGroup,
): boolean {
  if (row.status === 'cancelled' || row.status === 'rejected') return false;
  const dealId = row.dealId || row.costObject.dealId || '';
  const projectId = row.costObject.projectId || dealId;
  if (dealId !== group.dealId || projectId !== group.projectId) return false;
  const rowBucket = row.plBucket || row.costObject.plBucket;
  // Legacy design POs may omit plBucket; treat missing as design.
  if (!rowBucket) return group.plBucket === 'design';
  return rowBucket === group.plBucket;
}

export async function findPurchasesForDesignerInvoice(
  invoiceId: string,
): Promise<PurchaseRequisition[]> {
  const snap = await getDocs(
    query(
      collection(db, PURCHASE_REQUISITIONS_COLLECTION),
      where('designerInvoiceId', '==', invoiceId),
    ),
  );
  return snap.docs.map((d) => parsePurchaseRequisition(d.id, d.data() as Record<string, unknown>));
}

function paymentForInvoice(
  invoice: DesignerInvoiceDoc,
  lines: DesignerInvoiceLine[],
): { paymentMethodId: string; paymentMethod: string } {
  const phase = primaryDesignerInvoicePhase(lines, invoice.reviewPhase);
  const paymentMethodId = resolveDesignerInvoicePaymentMethodId({
    paymentMethodId: invoice.paymentMethodId,
    phase,
    amount: invoice.invoiceTotal,
  });
  return {
    paymentMethodId,
    paymentMethod: designerInvoicePaymentMethodLabel(paymentMethodId),
  };
}

async function issueGroup(args: {
  invoice: DesignerInvoiceDoc;
  group: DesignerInvoiceJobPoGroup;
  existing: PurchaseRequisition[];
  actor: { uid: string; name: string };
  paymentMethodId: string;
  paymentMethod: string;
}): Promise<IssuedDesignerInvoicePurchase> {
  const match = args.existing.find((row) => activeDesignerInvoicePurchase(row, args.group));
  if (match) {
    if (match.status === 'pending_approval' || match.status === 'in_review') {
      await approvePurchaseRequisition({
        requisition: match,
        actorId: args.actor.uid,
        actorName: args.actor.name,
        paymentMethod: args.paymentMethod,
        paymentMethodId: args.paymentMethodId,
        override: true,
      });
      const reloaded = await getPurchaseRequisition(match.id);
      return {
        purchaseRequisitionId: match.id,
        poNumber: reloaded?.poNumber || match.poNumber,
        dealId: args.group.dealId,
        projectId: args.group.projectId,
        amount: args.group.amount,
        reused: true,
      };
    }
    return {
      purchaseRequisitionId: match.id,
      poNumber: match.poNumber,
      dealId: args.group.dealId,
      projectId: args.group.projectId,
      amount: args.group.amount,
      reused: true,
    };
  }

  const draft = buildDesignerInvoiceJobPurchaseDraft({
    vendorName: args.invoice.vendor,
    invoiceNumber: args.invoice.invoiceNumber,
    invoiceDate: args.invoice.invoiceDate,
    group: args.group,
  });
  const attachment = designerInvoiceQuoteAttachment(args.invoice);
  const createSupplierBill = shouldCreateDesignerInvoiceSupplierBill({
    paymentMethodId: args.paymentMethodId,
    phases: args.group.lines.map((l) => l.phase),
  });
  const purchaseRequisitionId = await submitPurchaseRequisition({
    requesterId: args.actor.uid,
    requesterName: args.actor.name,
    title: draft.title,
    justification: draft.justification,
    vendorName: args.invoice.vendor,
    lineItems: draft.lineItems,
    salesTaxPct: 0,
    costObject: draft.costObject,
    steps: [designerInvoiceSelfIssueStep(args.actor)],
    bandLabel: 'Designer invoice',
    existingAttachments: attachment ? [attachment] : undefined,
    issueNow: true,
    paymentMethod: args.paymentMethod,
    paymentMethodId: args.paymentMethodId,
    designerInvoiceId: args.invoice.id,
    designerInvoiceNumber: args.invoice.invoiceNumber,
    createSupplierBill,
  });
  const created = await getPurchaseRequisition(purchaseRequisitionId);
  return {
    purchaseRequisitionId,
    poNumber: created?.poNumber,
    dealId: args.group.dealId,
    projectId: args.group.projectId,
    amount: args.group.amount,
    reused: false,
  };
}

/**
 * Rare client-side issue/backfill. Prefer server final-approve path.
 * recordAchPayableForIssuedPurchase only runs for ach_office (via afterPurchaseOrderIssued).
 * Bill Payment creates supplierBills on any phase; card methods stay PO-only.
 */
export async function issueDesignerInvoiceJobPurchases(args: {
  invoice: DesignerInvoiceDoc;
  lines: DesignerInvoiceLine[];
  actor: { uid: string; name: string };
}): Promise<IssueDesignerInvoicePurchasesResult> {
  if (!invoiceNeedsJobPurchaseOrders(args.lines)) {
    return { issued: [], qbSummaries: [] };
  }
  const groups = groupDesignerInvoiceJobPoLines(args.lines);
  if (!groups.length) return { issued: [], qbSummaries: [] };
  const { paymentMethodId, paymentMethod } = paymentForInvoice(args.invoice, args.lines);
  const existing = await findPurchasesForDesignerInvoice(args.invoice.id);
  const issued: IssuedDesignerInvoicePurchase[] = [];
  for (const group of groups) {
    issued.push(
      await issueGroup({
        invoice: args.invoice,
        group,
        existing,
        actor: args.actor,
        paymentMethodId,
        paymentMethod,
      }),
    );
  }
  return { issued, qbSummaries: [] };
}

export async function attemptDesignerInvoiceQboBills(args: {
  invoice: DesignerInvoiceDoc;
  issued: IssuedDesignerInvoicePurchase[];
  lines?: DesignerInvoiceLine[];
}): Promise<string[]> {
  const lines = args.lines || args.invoice.allocations || args.invoice.proposedLines || [];
  const phase = primaryDesignerInvoicePhase(lines, args.invoice.reviewPhase);
  const paymentMethodId = resolveDesignerInvoicePaymentMethodId({
    paymentMethodId: args.invoice.paymentMethodId,
    phase,
    amount: args.invoice.invoiceTotal,
  });
  if (
    !shouldCreateDesignerInvoiceQboBill({
      paymentMethodId,
      phases: lines.map((l) => l.phase),
    })
  ) {
    return [];
  }

  const ids = args.issued.map((row) => row.purchaseRequisitionId).filter(Boolean);
  if (!ids.length) return [];
  try {
    const data = await tryCreateDesignerInvoiceQboBills({
      invoiceId: args.invoice.id,
      purchaseRequisitionIds: ids,
    });
    const results = Array.isArray(data.results) ? data.results : [];
    return results.map((row) => {
      if (!row || typeof row !== 'object') return '';
      const r = row as Record<string, unknown>;
      const view = describePurchaseQbBill({
        status: typeof r.qbBillStatus === 'string' ? r.qbBillStatus : null,
        url: typeof r.qbBillUrl === 'string' ? r.qbBillUrl : null,
        message: typeof r.qbBillMessage === 'string' ? r.qbBillMessage : null,
        vendorName: args.invoice.vendor,
        invoiceNumber: args.invoice.invoiceNumber,
        amount: typeof r.amount === 'number' ? r.amount : undefined,
      });
      const po = typeof r.poNumber === 'string' ? r.poNumber : '';
      if (!view) return po ? `${po}: QuickBooks bill skipped` : '';
      return po ? `${po}: ${view.title}` : view.title;
    }).filter(Boolean);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'QuickBooks bill could not be created';
    await Promise.all(
      ids.map(async (id) => {
        await stampPurchaseQbBillStatus({
          requisitionId: id,
          qbBillStatus: 'needs_bill',
          qbBillMessage: message,
        }).catch(() => undefined);
        const bill = await findSupplierBillForPurchaseOrder(id).catch(() => null);
        if (!bill) return;
        await stampSupplierBillQbLink({
          bill,
          qbBillStatus: 'needs_bill',
          qbBillMessage: message,
          byUserId: 'system',
          byUserName: 'Designer invoice review',
        }).catch(() => undefined);
      }),
    );
    return [
      `Office must create the vendor and bill in QuickBooks for ${args.invoice.vendor || 'this vendor'}.`,
    ];
  }
}
